import { useState, useEffect, useCallback } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { projectsAPI } from '../services/api';
import { Column, Project } from '../types';
import { toast } from '../utils/toast';

type StageManagerUser = {
  id?: string;
  role?: string;
  email?: string;
} | null | undefined;

const DEFAULT_COLUMNS: Column[] = [
  { id: 'todo', title: 'To Do', color: '#9CA3AF', order: 0 },
  { id: 'in-progress', title: 'In Progress', color: '#3B82F6', order: 1 },
  { id: 'done', title: 'Done', color: '#10B981', order: 2 },
];

export function canManageProjectStages(project: Project, user: StageManagerUser): boolean {
  if (!user?.id) return false;
  const isAdmin = user.role === 'admin';
  const isMasterAdmin = user.role === 'masterAdmin' || user.email === 'admin@admin.com';
  const creatorId = (project.createdBy as any)?._id || (project.createdBy as any)?.id;
  return !!(isAdmin || isMasterAdmin || (creatorId && creatorId.toString() === user.id));
}

export function useProjectStages(
  projectId: string,
  project: Project,
  user: StageManagerUser,
  onProjectUpdate?: () => void,
) {
  const canManageStages = canManageProjectStages(project, user);
  const [columns, setColumns] = useState<Column[]>(
    project.columns?.length ? [...project.columns].sort((a, b) => a.order - b.order) : DEFAULT_COLUMNS,
  );
  const { open: stageDialogOpen, onOpen: onStageDialogOpen, onClose: onStageDialogClose } = useDisclosure();
  const { open: deleteDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure();
  const [newStageTitle, setNewStageTitle] = useState('');
  const [newStageColor, setNewStageColor] = useState('#9CA3AF');
  const [newStageSetAsFinished, setNewStageSetAsFinished] = useState(false);
  const [stageToDelete, setStageToDelete] = useState<string | null>(null);

  const reloadColumns = useCallback(async () => {
    try {
      const projectResponse = await projectsAPI.getById(projectId);
      if (projectResponse.data.columns?.length) {
        setColumns(projectResponse.data.columns.sort((a: Column, b: Column) => a.order - b.order));
      }
      onProjectUpdate?.();
    } catch {
      /* parent may refresh separately */
    }
  }, [projectId, onProjectUpdate]);

  useEffect(() => {
    if (project.columns?.length) {
      setColumns([...project.columns].sort((a, b) => a.order - b.order));
    }
  }, [project.columns]);

  const handleAddStage = async () => {
    if (!newStageTitle.trim()) {
      toast({ title: 'Error', description: 'Stage title is required', status: 'error' });
      return;
    }
    try {
      await projectsAPI.addColumn(projectId, newStageTitle, newStageColor, newStageSetAsFinished);
      setNewStageTitle('');
      setNewStageColor('#9CA3AF');
      setNewStageSetAsFinished(false);
      onStageDialogClose();
      await reloadColumns();
      toast({ title: 'Success', description: 'Stage added', status: 'success' });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to add stage',
        status: 'error',
      });
    }
  };

  const handleDeleteStageClick = (stageId: string) => {
    if (columns.length <= 1) {
      toast({ title: 'Error', description: 'Cannot delete the last stage', status: 'error' });
      return;
    }
    setStageToDelete(stageId);
    onDeleteDialogOpen();
  };

  const confirmDeleteStage = async () => {
    if (!stageToDelete) return;
    try {
      await projectsAPI.deleteColumn(projectId, stageToDelete);
      setStageToDelete(null);
      onDeleteDialogClose();
      await reloadColumns();
      toast({ title: 'Success', description: 'Stage deleted', status: 'success' });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete stage',
        status: 'error',
      });
      onDeleteDialogClose();
    }
  };

  const handleSetAsFinished = async (stageId: string) => {
    try {
      await projectsAPI.setFinishedColumn(projectId, stageId);
      await reloadColumns();
      toast({ title: 'Success', description: 'Finished stage updated', status: 'success' });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Could not set finished stage',
        status: 'error',
      });
    }
  };

  const handleReorderStage = async (stageId: string, direction: 'up' | 'down') => {
    const index = columns.findIndex((c) => c.id === stageId);
    if (index === -1) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= columns.length) return;

    const prevColumns = [...columns];
    const next = [...columns];
    const [moved] = next.splice(index, 1);
    next.splice(targetIndex, 0, moved);
    const reordered = next.map((c, i) => ({ ...c, order: i }));
    setColumns(reordered);

    try {
      await projectsAPI.reorderColumns(projectId, reordered.map((c) => c.id));
      onProjectUpdate?.();
    } catch (error: any) {
      setColumns(prevColumns);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Could not reorder stages',
        status: 'error',
      });
    }
  };

  const closeStageDialog = () => {
    setNewStageSetAsFinished(false);
    onStageDialogClose();
  };

  return {
    columns,
    setColumns,
    canManageStages,
    stageDialogOpen,
    onStageDialogOpen,
    closeStageDialog,
    deleteDialogOpen,
    onDeleteDialogClose,
    newStageTitle,
    setNewStageTitle,
    newStageColor,
    setNewStageColor,
    newStageSetAsFinished,
    setNewStageSetAsFinished,
    handleAddStage,
    handleDeleteStageClick,
    confirmDeleteStage,
    handleSetAsFinished,
    handleReorderStage,
  };
}
